import { observer } from "mobx-react-lite";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { useAppStore } from "@/store";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useCallback, useEffect, useState } from "react";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { subscriptionModule } from "@/modules/subscriptions";
import { api } from "@/modules/api";
import { DateTime } from "luxon";
import { PaidPlanManagement as FreePlanManagement } from "@/pages/settings/components/subscription-management/free";
import { SubscribedPlanManagement } from "@/pages/settings/components/subscription-management/subscribed";
import { CanceledPlanManagement } from "@/pages/settings/components/subscription-management/canceled";
import { LoadingSubscriptions } from "@/pages/settings/components/subscription-management/loading-subscriptions";
import {
  MDSModalDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSText,
  MDSTextVariant,
  MDSButton,
} from "@/mds";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { buildMonetizationUsagePayload } from "@/domains/metrics/monetization";
import { useLocation } from "react-router-dom";

export const SettingsSubscriptionManagementPage = observer(() => {
  const { store } = useAppStore();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [provider, setProvider] = useState<string | null>();
  const [proStatusText, setProStatusText] = useState<string | undefined>();
  const [planState, setPlanState] = useState<"free" | "subscribed" | "canceled">("free");
  const [downgradePreposition, setDowngradePreposition] = useState<string | undefined>();
  const nextMonthName = DateTime.local().plus({ months: 1 }).toFormat("LLLL");
  const renewalDateText = `${nextMonthName} 1st`;
  const subscriptionStatusText = planState === "free" ? undefined : proStatusText;
  const [isAppleManagedDialogOpen, setIsAppleManagedDialogOpen] = useState(false);

  useEffectOnMount(() => {
    (async () => {
      setIsLoading(true);

      try {
        await Promise.all([
          api.get("/v2/me/paywall/configuration"),
          store.platformInfo.fetchPlatformInfo(),
        ]);
      } finally {
        setIsLoading(false);
      }
    })();
  });

  const {
    currentPeriodEnd,
    isCanceled,
    isPaid,
    provider: subscriptionProvider,
    tier,
  } = store.subscriptions.personalSubscription ?? {};
  useEffect(() => {
    setProvider(subscriptionProvider ?? null);

    if (isPaid) {
      if (isCanceled) {
        const now = DateTime.local().startOf("day");
        if (currentPeriodEnd) {
          const periodEnd = DateTime.fromISO(currentPeriodEnd);
          const daysUntilPeriodEnd = Math.round(periodEnd.diff(now, "days").days);
          setDowngradePreposition(daysUntilPeriodEnd ? `in ${daysUntilPeriodEnd} days` : "soon");
          setProStatusText(`Unlimited until ${periodEnd.toFormat("LLLL d, yyyy")}`);
        } else {
          setDowngradePreposition("soon");
          setProStatusText("Unlimited until the end of your plan");
        }
        setPlanState("canceled");
      } else {
        setProStatusText("Unlimited");
        setPlanState("subscribed");
      }
    } else {
      setProStatusText(undefined);
      setPlanState("free");
    }
  }, [currentPeriodEnd, isCanceled, isPaid, subscriptionProvider, tier]);

  const handleSubscribe = useCallback(
    async (billingStrategy: BillingStrategyEnum) => {
      if (isSubscribing) return;
      setIsSubscribing(true);
      try {
        const monetizationProperties = await buildMonetizationUsagePayload(store);
        trackEvent(TrackedEvent.PaywallUpgradeClick, {
          context: "settings",
          ...monetizationProperties,
        });

        await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy,
          location,
          store,
        });
      } finally {
        setIsSubscribing(false);
      }
    },
    [isSubscribing, location, store]
  );

  const handleManageProPlan = useCallback(async () => {
    if (subscriptionModule.isAppleProvider(provider)) {
      setIsAppleManagedDialogOpen(true);
      return;
    }

    await subscriptionModule.getBillingPortalAndNavigate({ returnUrl: location });
  }, [provider, location]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Account" title="Subscription management" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          {isLoading ? (
            <LoadingSubscriptions />
          ) : planState === "free" ? (
            <FreePlanManagement renewalDateText={renewalDateText} onSubscribe={handleSubscribe} />
          ) : planState === "canceled" ? (
            <CanceledPlanManagement
              subscriptionStatusText={subscriptionStatusText || ""}
              downgradePreposition={downgradePreposition || "soon"}
              onSubscribe={() => handleSubscribe(BillingStrategyEnum.MONTHLY)}
            />
          ) : (
            <SubscribedPlanManagement onManageProPlan={handleManageProPlan} />
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>

      <MDSModalDialog
        isOpen={isAppleManagedDialogOpen}
        handleClose={() => setIsAppleManagedDialogOpen(false)}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Manage via App Store
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Your subscription was purchased through the Apple App Store. To manage your plan, please
            use your App Store subscription settings.
          </MDSText>
        </MDSDialogTextContent>
        <div slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton onClick={() => setIsAppleManagedDialogOpen(false)}>Close</MDSButton>
        </div>
      </MDSModalDialog>
    </SettingsIntegrationPageWrapper>
  );
});
